import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { Grid } from '@material-ui/core';
import { FiCamera } from 'react-icons/fi';

import api from '../../../../../../../services/api';

import no_image from '../../../../../../../assets/no_image.png';
import InputLogged from '../../../../../../../components/InputLogged';
import Select from '../../../../../../../components/Select';
import TextAreaLogged from '../../../../../../../components/TextAreaLogged';

import {
  useProdutoPizza,
  ProdutoPizzaData,
} from '../../../../../../../hooks/produtoPizza';
import { useToast } from '../../../../../../../hooks/toast';

import getValidationErrors from '../../../../../../../utils/getValidationErrors';
import resizeFile from '../../../../../../../utils/resizeFile';

import {
  TabWrapper,
  LabelWrapper,
  NewImg,
  ButtonAction,
} from '../../../drawer_styles';
import { SubmitButtonWrapper } from '../../../../styles';
import material_styles from '../../../drawer_styles/material_styles';
import { useAuth } from '../../../../../../../hooks/auth';

interface OptionType {
  id: string;
  value: string;
  label: string;
}

const Detalhes: React.FC = () => {
  const classes = material_styles();
  const formRef = useRef<FormHandles>(null);

  const {
    produtoId,
    produtoPizzaData,
    resetDrawer,
    enableTamanhos,
    changeProdutoPizzaData,
    changeCurrentTabIndex,
  } = useProdutoPizza();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [grupos, setGrupos] = useState<OptionType[]>([]);
  const [unidadesDeMedidas, setUnidadesDeMedidas] = useState<OptionType[]>([]);
  const [productImg, setProductImg] = useState<File | string>('');
  const [canSave, setCanSave] = useState(true);
  const [initialData, setInitialData] = useState<ProdutoPizzaData>();
  const [selectedUnidadeDeMedida, setSelectedUnidadeDeMedida] = useState<
    OptionType
  >();
  const [selectedGrupo, setSelectedGrupo] = useState<OptionType>();

  const handleImageChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];

        const image = await resizeFile(file);

        setProductImg(image);
      }
    },
    [],
  );

  useEffect(() => {
    setCanSave(false);

    async function loadGroups(): Promise<void> {
      const response = await api.get('/grupo/3');

      const optionItems = response.data.map((grupo: any) => ({
        id: grupo.id,
        value: grupo.id,
        label: grupo.descricao,
      }));

      setGrupos(optionItems);
    }

    async function loadUnidades(): Promise<void> {
      const response = await api.get('/unidade_medida');

      const optionItems = response.data.map((unidade_medida: any) => ({
        id: unidade_medida.id,
        value: unidade_medida.id,
        label: unidade_medida.sigla,
      }));

      setUnidadesDeMedidas(optionItems);

      setCanSave(true);
    }

    loadGroups();
    loadUnidades();
  }, []);

  const handleImage = useCallback(() => {
    if (productImg) {
      if (typeof productImg === 'string') {
        return `${process.env.REACT_APP_SERVER_URL}/files/${productImg}`;
      }

      return URL.createObjectURL(productImg);
    }

    return no_image;
  }, [productImg]);

  useEffect(() => {
    setProductImg(produtoPizzaData?.imagem || '');

    if (typeof produtoPizzaData.unidade_medida_id === 'number') {
      const unidadeDeMedida = unidadesDeMedidas.find(
        (option: any) =>
          String(option.id) === String(produtoPizzaData.unidade_medida_id),
      );

      if (unidadeDeMedida) {
        const { id, value, label } = unidadeDeMedida;

        setSelectedUnidadeDeMedida({ id, value, label });
      }
    } else {
      const unidadeDeMedida = unidadesDeMedidas.find(
        (unidade: any) => unidade.label === 'UN',
      );

      if (unidadeDeMedida) {
        const { id, value, label } = unidadeDeMedida;

        setSelectedUnidadeDeMedida({ id, value, label });
      }
    }

    const grupo = grupos.find(
      (option: any) => String(option.id) === String(produtoPizzaData.grupo_id),
    );

    setSelectedGrupo(grupo);
  }, [unidadesDeMedidas, grupos, produtoPizzaData]);

  useEffect(() => {
    setInitialData(produtoPizzaData);
  }, [produtoPizzaData]);

  const handleChangeSelectField = useCallback(
    (field: string, value: any) => {
      changeProdutoPizzaData({
        [field]: Number(value.id),
      });
    },
    [changeProdutoPizzaData],
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      setCanSave(false);

      const schema = Yup.object().shape({
        descricao: Yup.string().required('O nome do produto é obrigatório'),
        observacao: Yup.string().max(
          500,
          'A descrição do produto deve ter no máximo 500 caracteres',
        ),
      });

      try {
        await schema.validate(data, { abortEarly: false });

        if (data.gtin) {
          const hasGtinStored = await api.get(
            `/produto/${user.empresa_id}?gtin=${data.gtin}&produto_id=${produtoId}`,
          );

          if (hasGtinStored.data) {
            addToast({
              type: 'error',
              title:
                'Já existe um produto cadastrado com este código de barras.',
            });
            setCanSave(true);

            return;
          }
        }

        const { descricao, gtin, observacao } = data;

        changeProdutoPizzaData({
          descricao,
          gtin,
          observacao,
          imagem: productImg,
          grupo_id: Number(selectedGrupo?.id),
          unidade_medida_id: Number(selectedUnidadeDeMedida?.id),
        });
        setCanSave(true);
        enableTamanhos();
        changeCurrentTabIndex(1);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          addToast({
            type: 'error',
            title: 'Preencha os dados corretamente pra continuar',
          });

          setCanSave(true);

          return;
        }

        const { message } = err.response.data;

        addToast({
          type: 'error',
          title: message,
        });

        setCanSave(true);
      }
    },
    [
      user,
      productImg,
      produtoId,
      selectedGrupo,
      selectedUnidadeDeMedida,
      addToast,
      enableTamanhos,
      changeProdutoPizzaData,
      changeCurrentTabIndex,
    ],
  );

  const randomId = Math.random();

  return (
    <TabWrapper>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
        <Grid container spacing={3}>
          <Grid item xs={8} className={classes.noPaddingTop}>
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.noPaddingBot}>
                <LabelWrapper>
                  <span>Grupo:</span>
                </LabelWrapper>

                <Select
                  value={selectedGrupo}
                  name="grupo_id"
                  onChange={(value) =>
                    handleChangeSelectField('grupo_id', value)
                  }
                  placeholder="Selecione o grupo"
                  options={grupos}
                />
              </Grid>

              <Grid item xs={12} className={classes.noVerticalPadding}>
                <LabelWrapper>
                  <span>Nome do produto:</span>
                </LabelWrapper>

                <InputLogged
                  name="descricao"
                  placeholder="Nome do produto"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Descrição:</span>
                </LabelWrapper>
                <TextAreaLogged
                  name="observacao"
                  placeholder="Detalhes do produto e informações para o cliente."
                  rows={3}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={4} className={classes.noVerticalPadding}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <LabelWrapper>
                  <span>Imagem do Produto:</span>
                </LabelWrapper>
                <NewImg>
                  <img src={handleImage()} alt="Produto" />

                  <label htmlFor={String(randomId)}>
                    <FiCamera size={20} />
                    <input
                      type="file"
                      id={String(randomId)}
                      onChange={handleImageChange}
                    />
                  </label>
                </NewImg>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Código de barras:</span>
                </LabelWrapper>
                <InputLogged name="gtin" placeholder="Código de Barras" />
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Unidade de Medida:</span>
                </LabelWrapper>
                <Select
                  value={selectedUnidadeDeMedida}
                  name="unidade_medida_id"
                  placeholder="Selecione"
                  options={unidadesDeMedidas}
                  onChange={(value) =>
                    handleChangeSelectField('unidade_medida_id', value)
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.noPaddingBot}>
            <SubmitButtonWrapper>
              <ButtonAction
                type="button"
                className="cancel"
                onClick={resetDrawer}
              >
                Cancelar
              </ButtonAction>

              <ButtonAction
                type="submit"
                className="submit"
                id="submit_button"
                disabled={!canSave}
              >
                Continuar
              </ButtonAction>
            </SubmitButtonWrapper>
          </Grid>
        </Grid>
      </Form>
    </TabWrapper>
  );
};

export default Detalhes;
